import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, Plus, Search, UserPlus, Mail, Phone, X, Check, ChevronRight, ChevronLeft, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import ImportHubspotContactDialog from '@/components/customers/ImportHubspotContactDialog';
import { cn } from '@/lib/utils';

interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
}

interface SelectedContact {
  contact_id: string;
  contact_type: 'technical' | 'administrative' | 'billing' | 'pedagogical';
  contact: CustomerContact;
}

// Add icon definitions to CONTACT_TYPES
const CONTACT_TYPES = [
  { value: 'administrative', label: 'Administrativo', description: 'Responsável por questões administrativas', icon: <UserPlus className="h-5 w-5 text-purple-500" />, required: true },
  { value: 'billing', label: 'Faturamento', description: 'Responsável por questões financeiras e pagamentos', icon: <UserPlus className="h-5 w-5 text-green-500" />, required: true },
  { value: 'technical', label: 'Técnico', description: 'Responsável por questões técnicas e suporte', icon: <UserPlus className="h-5 w-5 text-blue-500" />, required: true },
  { value: 'pedagogical', label: 'Pedagógico', description: 'Responsável por questões educacionais e conteúdo', icon: <UserPlus className="h-5 w-5 text-orange-500" />, required: false },
];

const Step3Contacts = () => {
  const navigate = useNavigate();
  const { state, setSubscriptionContacts } = useSubscriptionWizard();
  
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<CustomerContact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>(state.selectedContacts || []);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 5;
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * contactsPerPage, 
    currentPage * contactsPerPage
  );
  
  // State for the Import Contact modal
  const [isImportContactDialogOpen, setIsImportContactDialogOpen] = useState(false);

  useEffect(() => {
    if (!state.selectedCustomer) {
      // Redirect back if previous steps are not completed
      navigate('../step1', { relative: 'path' });
      return;
    }
    
    fetchContacts();
  }, [state.selectedCustomer, navigate]);

  useEffect(() => {
    filterContacts();
    setCurrentPage(1); 
  }, [searchTerm, contacts]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // Get contacts for the selected customer and the related child customer 
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', state.selectedCustomer?.id)
        .eq('is_active', true);
      
      if (error) throw error;
      
      const fetchedContacts = data || [];
      setContacts(fetchedContacts);
      setFilteredContacts(fetchedContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    if (!searchTerm) {
      setFilteredContacts(contacts);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = contacts.filter(contact =>
      (contact.first_name?.toLowerCase().includes(searchLower)) ||
      (contact.last_name?.toLowerCase().includes(searchLower)) ||
      (contact.email?.toLowerCase().includes(searchLower))
    );
    setFilteredContacts(filtered);
  };

  const handleAddContact = (contact: CustomerContact, type: 'technical' | 'administrative' | 'billing' | 'pedagogical') => {
    const contactExists = selectedContacts.some(
      sc => sc.contact_id === contact.id && sc.contact_type === type
    );

    if (contactExists) {
      // Optionally provide feedback or do nothing
      // toast.info(`Este contato já foi adicionado como ${getContactTypeLabel(type)}`);
      return;
    }

    setSelectedContacts([
      ...selectedContacts,
      {
        contact_id: contact.id,
        contact_type: type,
        contact
      }
    ]);

    toast.success(`${getContactName(contact)} adicionado como contato ${getContactTypeLabel(type)}`);
  };

  const handleRemoveContact = (contactId: string, contactType: string) => {
    const contactToRemove = selectedContacts.find(
      sc => sc.contact_id === contactId && sc.contact_type === contactType
    );
    
    if (contactToRemove) {
      setSelectedContacts(
        selectedContacts.filter(
          sc => !(sc.contact_id === contactId && sc.contact_type === contactType)
        )
      );
      toast.success(`Contato ${getContactName(contactToRemove.contact)} removido`);
    }
  };

  const getContactTypeLabel = (type: string) => {
    const contactType = CONTACT_TYPES.find(ct => ct.value === type);
    return contactType ? contactType.label : type;
  };

  const getContactName = (contact: CustomerContact) => {
    return `${contact.first_name} ${contact.last_name || ''}`.trim();
  };

  const handleNext = () => {
    // Check only required contact types
    const requiredTypes = CONTACT_TYPES.filter(type => type.required);
    const missingRequiredTypes = requiredTypes.filter(type => 
      !selectedContacts.some(sc => sc.contact_type === type.value)
    );
    
    if (missingRequiredTypes.length > 0) {
      const missingLabels = missingRequiredTypes.map(type => type.label).join(', ');
      toast.error(`É necessário adicionar pelo menos um contato ${missingLabels}`);
      return;
    }
    setSubscriptionContacts(selectedContacts);
    navigate('../step4', { relative: 'path' });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getContactsByType = (type: string) => {
    return selectedContacts.filter(sc => sc.contact_type === type);
  };

  // Function to handle contact imported from dialog
  const handleContactImported = () => {
    fetchContacts(); // Refresh the contact list
  };

  return (
    <SubscriptionWizardLayout
      currentStep={3}
      totalSteps={5}
      title="Contatos da Assinatura"
      description="Selecione os contatos existentes ou importe do HubSpot"
      onNext={handleNext}
      onPrevious={() => {
        // For split subscriptions, go back to step2 (child customers)
        // For simple subscriptions, go back to step1c (subscription type)
        if (state.subscriptionType === 'split') {
          navigate('../step2', { relative: 'path' });
        } else {
          navigate('../step1c', { relative: 'path' });
        }
      }}
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Carregando contatos...</span>
        </div>
      ) : (
        <div className="grid md:grid-cols-7 gap-6">
          {/* Coluna da esquerda - Contatos disponíveis */}
          <div className="md:col-span-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                 <CardTitle>Contatos Disponíveis</CardTitle>
                 <CardDescription>
                   Selecione contatos existentes ou adicione novos
                 </CardDescription>
                  <div className="flex items-center gap-2 pt-2">
                    <div className="relative flex-grow">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar contato por nome ou email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                    <Button variant="outline" onClick={() => setIsImportContactDialogOpen(true)}>
                      <Download className="mr-2 h-4 w-4" />
                      Importar do HubSpot
                    </Button>
                  </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredContacts.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      {searchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato cadastrado'}
                    </div>
                  ) : (
                    <>
                      {paginatedContacts.map((contact) => {
                        const selectedTypes = selectedContacts
                          .filter(sc => sc.contact_id === contact.id)
                          .map(sc => sc.contact_type);

                        return (
                          <Card key={contact.id} className="overflow-hidden">
                            <CardContent className="p-3">
                              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-extrabold text-sm mb-1">{getContactName(contact)}</h3>
                                  <div className="flex items-center text-xs text-muted-foreground mb-1">
                                    <Mail className="h-3 w-3 mr-1" />
                                    {contact.email}
                                  </div>
                                </div>
                                
                                {/* Restore buttons for each type */}
                                <div className="flex items-center gap-1 shrink-0 mt-2 md:mt-0">
                                  {CONTACT_TYPES.map(type => {
                                    const isTypeSelected = selectedTypes.includes(type.value as any);
                                    
                                    return (
                                      <Button
                                        key={type.value}
                                        variant={isTypeSelected ? "default" : "secondary"} // Use default variant when selected
                                        size="sm"
                                        className="h-7 px-1.5"
                                        title={isTypeSelected 
                                          ? `Remover como contato ${type.label}` 
                                          : `Adicionar como contato ${type.label}`
                                        }
                                        onClick={() => {
                                          if (isTypeSelected) {
                                            handleRemoveContact(contact.id, type.value);
                                          } else {
                                            handleAddContact(contact, type.value as 'technical' | 'administrative' | 'billing' | 'pedagogical');
                                          }
                                        }}
                                      >
                                        {isTypeSelected ? (
                                          <Check className="h-4 w-4" /> // Show Check when selected
                                        ) : (
                                           React.cloneElement(type.icon, { className: type.icon.props.className.replace('h-5 w-5', 'h-4 w-4') })
                                        )}
                                      </Button>
                                    );
                                  })}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      
                      {/* Pagination */} 
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t pt-3 mt-4">
                          <div className="text-xs text-muted-foreground">
                            Página {currentPage} de {totalPages}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Coluna da direita - Resumo e contatos selecionados */}
          <div className="md:col-span-3 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Resumo de Contatos</CardTitle>
                <CardDescription>
                  Contatos necessários para prosseguir
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {CONTACT_TYPES.map(type => {
                  const typeContacts = getContactsByType(type.value);
                  const isComplete = typeContacts.length > 0;
                  
                  return (
                    <div key={type.value} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                           {/* Use the icon directly from CONTACT_TYPES */}
                           {/* {React.cloneElement(CONTACT_TYPES.find(ct => ct.value === type.value)?.icon || <UserPlus className="h-5 w-5" />, { className: "h-5 w-5" })} */}
                           {CONTACT_TYPES.find(ct => ct.value === type.value)?.icon}
                          <span className="font-extrabold">{type.label}</span>
                        </div>
                         <Badge variant={isComplete ? "default" : type.required ? "destructive" : "secondary"}>
                            {isComplete ? "Completo" : type.required ? "Obrigatório" : "Opcional"}
                         </Badge>
                      </div>
                      
                      {/* List selected contacts for this type */}
                      {isComplete ? (
                        <div className="pl-7 space-y-1">
                          {typeContacts.map(sc => (
                            <div key={`${sc.contact_id}-${sc.contact_type}`} className="flex items-center justify-between text-xs bg-muted rounded py-1 px-2">
                              <div className="flex items-center gap-2 overflow-hidden">
                                <span className="font-extrabold whitespace-nowrap">{getContactName(sc.contact)}</span>
                                <span className="text-muted-foreground truncate">{sc.contact.email}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 shrink-0"
                                onClick={() => handleRemoveContact(sc.contact_id, sc.contact_type)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                         <div className="text-center text-xs text-muted-foreground py-2 border border-dashed rounded">
                           Nenhum contato {type.label} selecionado
                         </div>
                      )}
                      
                      {/* Add separator between types */}
                      {type.value !== CONTACT_TYPES[CONTACT_TYPES.length - 1].value && (
                        <Separator className="my-3" />
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Import Contact Dialog */}
      {state.selectedCustomer && (
         <ImportHubspotContactDialog
           customerId={state.selectedCustomer.id}
           isOpen={isImportContactDialogOpen}
           onClose={() => setIsImportContactDialogOpen(false)}
           onContactImported={handleContactImported}
         />
      )}
    </SubscriptionWizardLayout>
  );
};

export default Step3Contacts; 